/**
 * AudioSystem — sample-backed SFX bank with oscillator fallback.
 *
 * Samples are generated at BUILD TIME by tools/generate-sfx.mjs, which calls
 * FlowState DAW's /api/generate-sfx endpoint (ElevenLabs Sound Effects v2 via
 * fal.ai). The shipping game makes NO network call for audio — same rule the
 * wave orchestrator and voice pipeline follow (bible docs/07, docs/10 §5.1).
 *
 * If public/sfx/manifest.json is absent the system degrades to the original
 * procedural oscillators, so the game is always playable without the asset run.
 *
 * Voice/SFX priority and ducking follow bible docs/10 §5.4.
 */

export type SfxId =
  // THE SWARM MOTHER. Her identity is many small sources, never one big one —
  // see tools/generate-sfx.mjs for why every prompt names that texture.
  | 'mother-curtain'
  | 'mother-lash'
  | 'mother-birth'
  | 'mother-iris-shut'
  | 'mother-morph'
  | 'mother-core-hit'
  | 'mother-death'
  | 'player-hailstorm'
  | 'player-arclight'
  | 'player-bomb-deploy'
  | 'player-death'
  | 'enemy-pellet'
  | 'enemy-fan'
  | 'drone-tick-swarm'
  | 'drone-picket-lock'
  | 'drone-dive'
  | 'drone-pop'
  | 'tank-charge'
  | 'explosion-medium'
  | 'explosion-large'
  | 'pickup-medal'
  | 'pickup-pod'
  | 'graze'
  | 'amb-farmland'
  | 'amb-wind-high'
  | 'echo-carrier'
  | 'echo-iff';

type ManifestEntry = { id: SfxId; file: string; duration: number };

/** Voice line IDs, generated by tools/generate-vo.mjs. */
export type VoId =
  | 'rios-launch' | 'rios-contact' | 'rios-chain' | 'rios-bomb' | 'rios-graze'
  | 'rios-boss' | 'rios-friendly-kill' | 'rios-low' | 'rios-death'
  | 'rios-death-2' | 'rios-death-3' | 'rios-death-4' | 'rios-death-5'
  | 'rios-clear' | 'rios-echo'
  | 'porch-launch' | 'porch-corridor' | 'porch-bridge' | 'porch-echo'
  | 'porch-base' | 'porch-clear'
  | 'porch-ally-inbound' | 'porch-ally-guns' | 'porch-ally-resupply'
  | 'porch-ally-lost' | 'porch-midboss'
  | 'rios-ally-thanks' | 'rios-missiles-hot'
  | 'wight-brief'
  | 'echo-mayday';

type VoManifestEntry = { id: VoId; who: string; file: string; text: string };

/** Music states. The runtime crossfades between them; it never hard-cuts. */
export type MusicId =
  | 'home-field'
  | 'home-field-surge'
  | 'boss-battery-nine'
  | 'boss-swarm-mother'
  | 'boss-swarm-mother-collapse';
type MusicManifestEntry = { id: MusicId; file: string; duration: number };

/** docs/10 §5.4 — lower number wins and ducks the rest. */
const PRIORITY: Partial<Record<SfxId, number>> = {
  'echo-carrier': 0,
  'echo-iff': 0,
  'player-death': 1,
  'explosion-large': 1,
};

/**
 * Rate limits, in ms. A bullet-hell fires 8-10 shots/sec; retriggering a full
 * sample every shot is mud. Cap the retrigger and let the tail breathe.
 */
const MIN_INTERVAL: Partial<Record<SfxId, number>> = {
  'player-hailstorm': 90,
  'player-arclight': 110,
  'enemy-pellet': 60,
  'drone-pop': 40,
  graze: 120,
};

export class AudioSystem {
  private context: AudioContext | null = null;
  private unlocked = false;
  private master: GainNode | null = null;
  private duckGain: GainNode | null = null;

  private buffers = new Map<SfxId, AudioBuffer>();
  private voBuffers = new Map<VoId, AudioBuffer>();
  private lastPlayed = new Map<SfxId, number>();
  private loops = new Map<SfxId, AudioBufferSourceNode>();
  private loaded = false;
  /** Voice is monophonic by design — one speaker at a time, like a real net. */
  private voActive: AudioBufferSourceNode | null = null;
  private voPriority = 99;
  private spokenOnce = new Set<VoId>();

  // Music: two decks so tracks can crossfade rather than cut.
  private musicBuffers = new Map<MusicId, AudioBuffer>();
  private musicBus: GainNode | null = null;
  private deckA: { src: AudioBufferSourceNode; gain: GainNode; id: MusicId } | null = null;
  private musicTarget: MusicId | null = null;

  constructor() {
    const unlock = () => {
      void this.unlock();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }

  async unlock(): Promise<void> {
    if (this.unlocked) return;
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;

    this.context = new AudioContextClass();
    await this.context.resume();

    // master <- duck <- sources. Ducking a single node keeps it cheap.
    this.master = this.context.createGain();
    this.master.gain.value = 0.8;
    this.duckGain = this.context.createGain();
    this.duckGain.gain.value = 1;
    this.duckGain.connect(this.master).connect(this.context.destination);

    // Music sits on its own bus so voice ducking can pull it down without
    // touching SFX, and so the player can have a separate music slider later.
    this.musicBus = this.context.createGain();
    this.musicBus.gain.value = 0.42;
    this.musicBus.connect(this.master);

    this.unlocked = true;
    void this.loadBank();
  }

  /** Decode the whole bank up front. It is a few hundred KB total. */
  private async loadBank(): Promise<void> {
    if (!this.context || this.loaded) return;
    try {
      const res = await fetch('/sfx/manifest.json');
      if (!res.ok) return; // No bank generated yet — fallback stays active.
      const manifest: ManifestEntry[] = await res.json();

      await Promise.all(
        manifest.map(async (entry) => {
          try {
            const r = await fetch(`/${entry.file}`);
            if (!r.ok) return;
            const buf = await this.context!.decodeAudioData(await r.arrayBuffer());
            this.buffers.set(entry.id, buf);
          } catch {
            /* one bad file must not sink the bank */
          }
        }),
      );
      this.loaded = this.buffers.size > 0;
    } catch {
      /* offline or not generated — fallback stays active */
    }

    // Voice bank. Independent of SFX: a missing VO bank must never disable SFX.
    try {
      const res = await fetch('/vo/manifest.json');
      if (!res.ok) return;
      const manifest: VoManifestEntry[] = await res.json();
      await Promise.all(
        manifest.map(async (entry) => {
          try {
            const r = await fetch(`/${entry.file}`);
            if (!r.ok) return;
            this.voBuffers.set(entry.id, await this.context!.decodeAudioData(await r.arrayBuffer()));
          } catch {
            /* skip one bad line */
          }
        }),
      );
    } catch {
      /* no VO bank generated yet */
    }

    // Music bank.
    try {
      const res = await fetch('/music/manifest.json');
      if (!res.ok) return;
      const manifest: MusicManifestEntry[] = await res.json();
      await Promise.all(
        manifest.map(async (entry) => {
          try {
            const r = await fetch(`/${entry.file}`);
            if (!r.ok) return;
            this.musicBuffers.set(entry.id, await this.context!.decodeAudioData(await r.arrayBuffer()));
          } catch {
            /* skip one bad track */
          }
        }),
      );
    } catch {
      /* no music generated yet */
    }
  }

  get hasMusic(): boolean {
    return this.musicBuffers.size > 0;
  }

  /**
   * Crossfade to a track. Idempotent — calling it every frame with the same id
   * is a no-op, which lets the game loop express music as a pure function of
   * game state rather than as a sequence of events.
   */
  playMusic(id: MusicId, fadeSeconds = 1.6): void {
    if (!this.context || !this.musicBus || this.context.state !== 'running') return;
    if (this.musicTarget === id) return;
    const buffer = this.musicBuffers.get(id);
    if (!buffer) return;

    this.musicTarget = id;
    const now = this.context.currentTime;

    // Fade the outgoing deck out and let it stop itself.
    if (this.deckA) {
      const old = this.deckA;
      old.gain.gain.cancelScheduledValues(now);
      old.gain.gain.setValueAtTime(old.gain.gain.value, now);
      old.gain.gain.linearRampToValueAtTime(0.0001, now + fadeSeconds);
      window.setTimeout(() => {
        try { old.src.stop(); } catch { /* already ended */ }
      }, fadeSeconds * 1000 + 120);
    }

    const src = this.context.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const gain = this.context.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(1, now + fadeSeconds);
    src.connect(gain).connect(this.musicBus);
    src.start(now);
    this.deckA = { src, gain, id };
  }

  stopMusic(fadeSeconds = 1.2): void {
    if (!this.context || !this.deckA) return;
    const now = this.context.currentTime;
    const old = this.deckA;
    old.gain.gain.linearRampToValueAtTime(0.0001, now + fadeSeconds);
    window.setTimeout(() => {
      try { old.src.stop(); } catch { /* already ended */ }
    }, fadeSeconds * 1000 + 120);
    this.deckA = null;
    this.musicTarget = null;
  }

  /**
   * Speak a line. Monophonic with priority: ECHO (0) interrupts anything,
   * story comms (1) interrupt barks, barks (3) are dropped if anything is
   * already talking. Mirrors bible docs/10 §5.4 — a missed bark costs nothing,
   * a missed ECHO line costs the scene.
   */
  say(id: VoId, opts: { priority?: number; once?: boolean; gain?: number } = {}): void {
    if (!this.context || !this.duckGain || this.context.state !== 'running') return;
    const buffer = this.voBuffers.get(id);
    if (!buffer) return;

    const priority = opts.priority ?? (id.startsWith('echo') ? 0 : id.startsWith('rios') ? 3 : 1);
    if (opts.once) {
      if (this.spokenOnce.has(id)) return;
      this.spokenOnce.add(id);
    }
    // Never talk over something more important than this.
    if (this.voActive && priority >= this.voPriority) return;
    if (this.voActive) {
      try { this.voActive.stop(); } catch { /* already ended */ }
    }

    const now = this.context.currentTime;
    const src = this.context.createBufferSource();
    src.buffer = buffer;
    const g = this.context.createGain();
    g.gain.value = opts.gain ?? 1;
    src.connect(g).connect(this.duckGain);
    src.onended = () => {
      if (this.voActive === src) {
        this.voActive = null;
        this.voPriority = 99;
      }
    };
    src.start(now);
    this.voActive = src;
    this.voPriority = priority;

    // Voice ducks the SFX bed so dialogue stays intelligible under fire.
    const duck = priority === 0 ? 0.45 : 0.62;
    const d = this.duckGain.gain;
    d.cancelScheduledValues(now);
    d.setValueAtTime(d.value, now);
    d.linearRampToValueAtTime(duck, now + 0.05);
    d.linearRampToValueAtTime(1, now + buffer.duration + 0.15);
  }

  /**
   * Speak one line at random from a set. Death and kill-chain barks repeat
   * often enough that a single fixed line stops registering as drama and
   * starts registering as a bug.
   */
  sayOneOf(ids: VoId[], opts: { priority?: number; gain?: number } = {}): void {
    const available = ids.filter((id) => this.voBuffers.has(id));
    if (available.length === 0) return;
    this.say(available[Math.floor(Math.random() * available.length)], opts);
  }

  get hasVoice(): boolean {
    return this.voBuffers.size > 0;
  }

  get hasSamples(): boolean {
    return this.loaded;
  }

  /**
   * Fire a one-shot.
   * @param gain 0-1 relative level
   * @param pan  -1 left .. 1 right, used to place enemies laterally
   */
  play(id: SfxId, gain = 1, pan = 0): void {
    if (!this.context || !this.duckGain || this.context.state !== 'running') return;

    const now = this.context.currentTime;
    const nowMs = now * 1000;
    const min = MIN_INTERVAL[id];
    if (min !== undefined) {
      const last = this.lastPlayed.get(id) ?? -Infinity;
      if (nowMs - last < min) return;
      this.lastPlayed.set(id, nowMs);
    }

    const buffer = this.buffers.get(id);
    if (!buffer) {
      this.fallback(id, gain);
      return;
    }

    const src = this.context.createBufferSource();
    src.buffer = buffer;

    const g = this.context.createGain();
    g.gain.value = gain;

    let tail: AudioNode = g;
    if (pan !== 0 && this.context.createStereoPanner) {
      const p = this.context.createStereoPanner();
      p.pan.value = Math.max(-1, Math.min(1, pan));
      g.connect(p);
      tail = p;
    }

    src.connect(g);
    tail.connect(this.duckGain);
    src.start(now);

    // Priority 0/1 ducks everything else briefly (docs/10 §5.4).
    const prio = PRIORITY[id];
    if (prio !== undefined && prio <= 1) {
      const duck = prio === 0 ? 0.5 : 0.66; // -6 dB / -4 dB
      const d = this.duckGain.gain;
      d.cancelScheduledValues(now);
      d.setValueAtTime(d.value, now);
      d.linearRampToValueAtTime(duck, now + 0.04);
      d.linearRampToValueAtTime(1, now + Math.min(buffer.duration, 1.2));
    }
  }

  /** Start a seamless ambience loop. Idempotent. */
  loop(id: SfxId, gain = 0.35): void {
    if (!this.context || !this.duckGain || this.loops.has(id)) return;
    const buffer = this.buffers.get(id);
    if (!buffer) return;

    const src = this.context.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const g = this.context.createGain();
    g.gain.value = gain;
    src.connect(g).connect(this.duckGain);
    src.start();
    this.loops.set(id, src);
  }

  stopLoop(id: SfxId): void {
    const src = this.loops.get(id);
    if (!src) return;
    try {
      src.stop();
    } catch {
      /* already stopped */
    }
    this.loops.delete(id);
  }

  /** Legacy hook retained so existing call sites keep working. */
  pickup(index: number): void {
    if (this.buffers.has('pickup-medal')) {
      this.play('pickup-medal', 0.8, 0);
      return;
    }
    this.fallbackPickup(index);
  }

  /** Procedural stand-ins so the game is never silent before the asset run. */
  private fallback(id: SfxId, gain: number): void {
    if (!this.context || !this.duckGain) return;
    const now = this.context.currentTime;
    const osc = this.context.createOscillator();
    const g = this.context.createGain();

    const shape: Record<string, [OscillatorType, number, number, number]> = {
      'player-hailstorm': ['square', 220, 90, 0.06],
      'player-arclight': ['sawtooth', 620, 900, 0.07],
      'enemy-pellet': ['triangle', 180, 120, 0.05],
      'drone-pop': ['square', 400, 80, 0.08],
      'explosion-medium': ['sawtooth', 120, 40, 0.12],
      'explosion-large': ['sawtooth', 90, 30, 0.16],
      'player-death': ['sawtooth', 300, 40, 0.18],
    };
    const [type, f0, f1, amp] = shape[id] ?? ['triangle', 440, 440, 0.05];

    osc.type = type;
    osc.frequency.setValueAtTime(f0, now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), now + 0.14);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, amp * gain), now + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
    osc.connect(g).connect(this.duckGain);
    osc.start(now);
    osc.stop(now + 0.22);
  }

  private fallbackPickup(index: number): void {
    if (!this.context || !this.duckGain) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    const now = this.context.currentTime;

    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(320 + index * 22, now);
    oscillator.frequency.exponentialRampToValueAtTime(680 + index * 24, now + 0.12);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    oscillator.connect(gain).connect(this.duckGain);
    oscillator.start(now);
    oscillator.stop(now + 0.2);
  }

  dispose(): void {
    this.stopMusic(0.05);
    for (const id of [...this.loops.keys()]) this.stopLoop(id);
    void this.context?.close();
    this.context = null;
  }
}
